import { useCallback } from "react";
import notify from "devextreme/ui/notify";
import { removeAllCachedForms } from "../../../utils/formCache.js";
import { getParameterFromUrl } from "../utils/urlUtils.js";
import { downloadFile, buildDraftPayload } from "../services/formFileService.js";
import { saveChangesToServer } from "../services/formApiService.js";

export default function useFormActions({ docRef, state, updateState, clearUnvisibleData, executeFunctionByUid }) {

  const onSaveFile = useCallback(() => {
    clearUnvisibleData();
    executeFunctionByUid("RunBeforeSaveFile");
    downloadFile(docRef.current, state.dataFromDB?.FormName);
  }, [docRef, state.dataFromDB, clearUnvisibleData, executeFunctionByUid]);

  const saveAndReset = useCallback(() => {
    onSaveFile();
    window.location.reload();
    updateState({ refreshPopupVisible: false });
  }, [onSaveFile, updateState]);

  const deleteAndReset = useCallback(() => {
    removeAllCachedForms();
    window.location.reload();
    updateState({ refreshPopupVisible: false });
  }, [updateState]);

  const copyUrl = useCallback(async () => {
    const copyText =
      window.location.origin +
      `/#/client-login?u=${state.dataFromDB.UrlToApi.substring(35)}&i=${state.user.phone}`;
    notify(
      {
        message: "העתקת את הקישור ללקוח",
        position: { my: "center top", at: "center top" },
      },
      "info",
      3000
    );
    return await navigator.clipboard.writeText(copyText);
  }, [state.dataFromDB, state.user]);

  const createUser = useCallback(() => {
    const userData = state.user;
    return {
      avatarUrl: userData.avatarUrl,
      phone: userData.phone,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      UserWrikeId: userData.UserWrikeId,
    };
  }, [state.user]);

  const onSaveChanges = useCallback(async (status) => {
    if (status === "תבנית" && !state.draftName) return;

    updateState({ showPopup: false });
    notify({ message: "בתהליך שמירת נתונים" }, "warning", 3000);

    downloadFile(docRef.current, state.dataFromDB?.FormName);
    const formId = getParameterFromUrl("f");
    const payload = buildDraftPayload(docRef.current, {
      taskId: getParameterFromUrl("t"),
      formId,
      formName: state.dataFromDB?.FormName,
      status,
      draftName: state.draftName,
    });

    try {
      const result = await saveChangesToServer(payload);
      if (result.ok) {
        if (!String(result.id).includes('!')) {
          window.location.href = "/#/form?f=" + formId + "&t=" + result.id;
        }
        updateState({ sourceStatus: result.status, isLoading: false });
        notify({ message: "השינויים נשמרו בהצלחה" }, "success", 3000);
      } else {
        notify({ message: "חלה תקלה בשמירת הנתונים" }, "error", 3000);
      }
    } catch (err) {
      console.error("[onSaveChanges] failed:", err);
      notify({ message: "שמירה נכשלה" }, "error", 3000);
    }
  }, [docRef, state.dataFromDB, state.draftName, updateState]);

  // Silent draft auto-save: no file download, no toast, no hard redirect.
  // Reuses the same payload builder + endpoint as onSaveChanges.
  // By default it only UPDATES an existing draft (a task id must already be in the URL) —
  // it never silently creates a new draft. Pass { allowCreate: true } to create one
  // (used when the user explicitly opts in via the prompt); the new task id is then
  // recorded in the URL via replaceState so subsequent saves update the same draft.
  const autoSaveDraft = useCallback(async (opts = {}) => {
    // Templates are saved explicitly, never auto-saved as drafts.
    if (state.sourceStatus === "תבנית") return false;

    const existingTask = getParameterFromUrl("t");
    // Never create a draft automatically — only update an existing one.
    if (!existingTask && !opts.allowCreate) return false;

    const formId = getParameterFromUrl("f");
    let payload;
    try {
      payload = buildDraftPayload(docRef.current, {
        taskId: getParameterFromUrl("t"),
        formId,
        formName: state.dataFromDB?.FormName,
        status: "טיוטה",
        draftName: state.draftName,
      });
    } catch (err) {
      console.warn("[autoSaveDraft] skipped (payload build failed):", err);
      return false;
    }

    updateState({ isAutoSaving: true });
    try {
      const result = await saveChangesToServer(payload);
      if (result.ok) {
        // First save returns a new task id — put it in the URL without navigating,
        // so subsequent saves are updates rather than new drafts.
        if (result.id && !String(result.id).includes("!") && !existingTask) {
          const { origin, pathname, search } = window.location;
          const newUrl = `${origin}${pathname}${search}#/form?f=${formId}&t=${result.id}`;
          window.history.replaceState(window.history.state, "", newUrl);
        }
        updateState({
          isAutoSaving: false,
          lastSavedAt: Date.now(),
          sourceStatus: result.status || state.sourceStatus,
        });
        return true;
      }
      updateState({ isAutoSaving: false });
      return false;
    } catch (err) {
      console.warn("[autoSaveDraft] failed:", err);
      updateState({ isAutoSaving: false });
      return false;
    }
  }, [docRef, state.dataFromDB, state.draftName, state.sourceStatus, updateState]);

  const hidePopUp = useCallback(() => {
    updateState({ refreshPopupVisible: false, sendApendixPopupVisible: false });
  }, [updateState]);

  return {
    onSaveFile,
    saveAndReset,
    deleteAndReset,
    copyUrl,
    createUser,
    onSaveChanges,
    autoSaveDraft,
    hidePopUp,
  };
}
